import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";

const ESTADOS = ["pendiente", "confirmada", "cancelada"];

export default function CrmReservas() {
    const [reservas, setReservas] = useState([]);
    const [filtroEstado, setFiltroEstado] = useState("");
    const [filtroFecha, setFiltroFecha] = useState("");
    const [loading, setLoading] = useState(true);

    const load = () => {
        setLoading(true);
        const params = {};
        if (filtroEstado) params.estado = filtroEstado;
        if (filtroFecha) params.fecha = filtroFecha;
        api.get("/reservas", { params })
            .then((r) => setReservas(r.data))
            .catch((err) => toast.error(formatApiError(err)))
            .finally(() => setLoading(false));
    };

    useEffect(load, [filtroEstado, filtroFecha]);

    const updateEstado = async (id, estado) => {
        try {
            await api.patch(`/reservas/${id}`, { estado });
            toast.success(`Reserva ${estado}`);
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const remove = async (id) => {
        if (!window.confirm("¿Eliminar esta reserva?")) return;
        try {
            await api.delete(`/reservas/${id}`);
            toast.success("Eliminada");
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    return (
        <div className="space-y-10">
            <header className="flex items-end justify-between flex-wrap gap-6">
                <div>
                    <p className="font-body uppercase tracking-[0.3em] text-xs text-dorado mb-3">
                        Reservas
                    </p>
                    <h1 className="font-heading text-5xl text-crema font-light">
                        Gestión de mesas
                    </h1>
                </div>
                <div className="flex items-end gap-4">
                    <div>
                        <label className="line-label">Estado</label>
                        <select
                            value={filtroEstado}
                            onChange={(e) => setFiltroEstado(e.target.value)}
                            className="input-line bg-carbon min-w-[140px]"
                            data-testid="filter-estado"
                        >
                            <option value="">Todos</option>
                            {ESTADOS.map((e) => (
                                <option key={e} value={e}>
                                    {e}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="line-label">Fecha</label>
                        <input
                            type="date"
                            value={filtroFecha}
                            onChange={(e) => setFiltroFecha(e.target.value)}
                            className="input-line"
                            data-testid="filter-fecha"
                        />
                    </div>
                </div>
            </header>

            {loading ? (
                <p className="text-piedra italic font-body">Cargando...</p>
            ) : reservas.length === 0 ? (
                <p className="text-piedra italic font-body">No hay reservas con esos filtros.</p>
            ) : (
                <div className="border border-dorado/20 bg-carbon-suave/40">
                    <div className="hidden md:grid grid-cols-12 gap-3 px-5 py-3 border-b border-dorado/15 font-body text-[0.65rem] uppercase tracking-[0.25em] text-piedra">
                        <div className="col-span-3">Cliente</div>
                        <div className="col-span-2">Fecha</div>
                        <div className="col-span-1">Hora</div>
                        <div className="col-span-1">Pax</div>
                        <div className="col-span-2">Contacto</div>
                        <div className="col-span-2">Estado</div>
                        <div className="col-span-1 text-right">Acciones</div>
                    </div>
                    <div className="divide-y divide-dorado/10">
                        {reservas.map((r) => (
                            <div
                                key={r.id}
                                className="grid grid-cols-1 md:grid-cols-12 gap-3 px-5 py-4 items-center"
                                data-testid="crm-reserva-row"
                            >
                                <div className="col-span-3">
                                    <p className="font-heading text-lg text-crema">{r.nombre}</p>
                                    {r.comentarios && (
                                        <p className="font-body text-xs text-piedra italic mt-1">
                                            "{r.comentarios}"
                                        </p>
                                    )}
                                </div>
                                <div className="col-span-2 font-body text-sm text-crema">
                                    {r.fecha}
                                </div>
                                <div className="col-span-1 font-heading italic text-dorado text-lg">
                                    {r.hora}
                                </div>
                                <div className="col-span-1 font-body text-sm text-piedra">
                                    {r.personas}
                                </div>
                                <div className="col-span-2 font-body text-xs text-piedra">
                                    {r.email && <div>{r.email}</div>}
                                    {r.telefono && <div>{r.telefono}</div>}
                                </div>
                                <div className="col-span-2">
                                    <select
                                        value={r.estado}
                                        onChange={(e) => updateEstado(r.id, e.target.value)}
                                        data-testid="crm-status-update"
                                        className={`input-line bg-carbon text-xs py-2 ${
                                            r.estado === "confirmada"
                                                ? "text-dorado"
                                                : r.estado === "cancelada"
                                                ? "text-red-400"
                                                : "text-piedra"
                                        }`}
                                    >
                                        {ESTADOS.map((e) => (
                                            <option key={e} value={e}>
                                                {e}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="col-span-1 text-right">
                                    <button
                                        onClick={() => remove(r.id)}
                                        className="text-piedra hover:text-red-400"
                                        aria-label="Eliminar"
                                        data-testid="crm-reserva-delete"
                                    >
                                        <Trash2 size={16} />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
